import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Assessment,
  Science,
  Edit,
  Settings,
  Refresh,
  Speed,
  Category
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../../../../API';

export default function ClassificationManagementTab({ showToast }) {
  const [testEmail, setTestEmail] = useState('');
  const [testSubject, setTestSubject] = useState('');
  const [testSender, setTestSender] = useState('');
  const [testResult, setTestResult] = useState(null);
  
  const [bulkReclassifyData, setBulkReclassifyData] = useState({
    creator_id: '',
    old_category: '',
    new_category: '',
    sender_pattern: '',
    subject_pattern: '',
    reason: '',
    dry_run: true
  });
  
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configData, setConfigData] = useState({
    confidence_threshold: 0.60,
    enable_hybrid_classifier: true,
    timeout_seconds: 30
  });

  const queryClient = useQueryClient();

  // Query: Classification Stats
  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['classificationStats'],
    queryFn: () => API.blitzAI.getClassificationStats(24),
    refetchInterval: 60000, // Refresh every minute
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to load classification stats', 'error');
    }
  });

  // Query: Classifier Config
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['classifierConfig'],
    queryFn: () => API.blitzAI.getClassifierConfig(),
    onSuccess: (data) => {
      if (data?.config) {
        setConfigData(data.config);
      }
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to load classifier config', 'error');
    }
  });

  // Mutation: Test Classification
  const testClassifyMutation = useMutation(
    (data) => API.blitzAI.testClassification(data),
    {
      onSuccess: (data) => {
        setTestResult(data.classification);
        showToast('Classification test completed', 'success');
      },
      onError: (error) => {
        showToast(error.response?.data?.error || 'Classification test failed', 'error');
      }
    }
  );

  // Mutation: Bulk Reclassify
  const bulkReclassifyMutation = useMutation(
    (data) => API.blitzAI.bulkReclassify(data),
    {
      onSuccess: (data) => {
        showToast(
          data.dry_run 
            ? `Found ${data.matched_emails || 0} emails matching filters` 
            : `Successfully reclassified ${data.updated_emails || 0} emails`,
          'success'
        );
      },
      onError: (error) => {
        showToast(error.response?.data?.error || 'Bulk reclassification failed', 'error');
      }
    }
  );

  // Mutation: Update Config
  const updateConfigMutation = useMutation(
    (data) => API.blitzAI.updateClassifierConfig(data),
    {
      onSuccess: (data) => {
        showToast('Configuration updated successfully', 'success');
        if (data.restart_required) {
          showToast('Note: Service restart required for changes to take effect', 'warning');
        }
        queryClient.invalidateQueries(['classifierConfig']);
        setConfigDialogOpen(false);
      },
      onError: (error) => {
        showToast(error.response?.data?.error || 'Failed to update configuration', 'error');
      }
    }
  );

  const handleTestClassify = () => {
    if (!testEmail.trim()) {
      showToast('Please enter email content to test', 'warning');
      return;
    }

    testClassifyMutation.mutate({
      email_content: testEmail,
      subject: testSubject,
      sender: testSender
    });
  };

  const handleBulkReclassify = () => {
    if (!bulkReclassifyData.new_category) {
      showToast('Please select a new category', 'warning');
      return;
    }

    const filters = {};
    if (bulkReclassifyData.creator_id) filters.creator_id = bulkReclassifyData.creator_id;
    if (bulkReclassifyData.old_category) filters.old_category = bulkReclassifyData.old_category;
    if (bulkReclassifyData.sender_pattern) filters.sender_pattern = bulkReclassifyData.sender_pattern;
    if (bulkReclassifyData.subject_pattern) filters.subject_pattern = bulkReclassifyData.subject_pattern;

    bulkReclassifyMutation.mutate({
      filters,
      new_category: bulkReclassifyData.new_category,
      reason: bulkReclassifyData.reason,
      dry_run: bulkReclassifyData.dry_run
    });
  };

  const handleUpdateConfig = () => {
    updateConfigMutation.mutate(configData);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Paid': 'success',
      'Affiliate': 'secondary',
      'Agency': 'info',
      'Platform': 'primary',
      'Gifting': 'warning',
      'ignore-thread': 'default'
    };
    return colors[category] || 'default';
  };

  const getConfidenceTierColor = (tier) => {
    if (tier === 'high') return 'success';
    if (tier === 'medium') return 'warning';
    return 'error';
  };

  const validCategories = [
    'Paid', 'Affiliate', 'Agency', 'Platform', 'Gifting',
    'Music', 'General', 'Meeting', 'External', 'APAC',
    'FanMail', 'ignore-thread'
  ];

  // Show loading state
  if (statsLoading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (statsError && !stats) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load classification statistics. Please try refreshing the page.
        </Alert>
        <Button variant="contained" startIcon={<Refresh />} onClick={() => refetchStats()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Classifier Performance */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Classifier Performance</Typography>
                <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => refetchStats()}>
                  <Refresh />
                </IconButton>
              </Box>

              {statsLoading ? (
                <LinearProgress />
              ) : stats?.classifier_stats ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Keyword Match Rate
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {((stats.classifier_stats.keyword_match_rate || 0) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.classifier_stats.keyword_matches || 0} / {stats.classifier_stats.total_classified || 0} emails
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Avg Processing Time
                    </Typography>
                    <Typography variant="h5">
                      {(stats.classifier_stats.avg_processing_time_ms || 0).toFixed(0)}ms
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Database Stats */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Category color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Database Stats (24h)</Typography>
              </Box>

              {statsLoading ? (
                <LinearProgress />
              ) : stats?.database_stats ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Emails
                    </Typography>
                    <Typography variant="h4">
                      {stats.database_stats.total_emails_24h || 0}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {stats.database_stats.categories && Object.entries(stats.database_stats.categories).length > 0 ? (
                      Object.entries(stats.database_stats.categories).map(([cat, count]) => (
                        <Chip
                          key={cat}
                          label={`${cat}: ${count}`}
                          size="small"
                          color={getCategoryColor(cat)}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No categories yet
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Performance</Typography>
              </Box>

              {statsLoading ? (
                <LinearProgress />
              ) : stats?.performance ? (
                <Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Emails per Hour
                    </Typography>
                    <Typography variant="h5">
                      {(stats.performance.emails_per_hour || 0).toFixed(1)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Latency P50
                    </Typography>
                    <Typography variant="body2">
                      {(stats.performance.classification_latency_p50 || 0).toFixed(0)}ms
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Latency P95
                    </Typography>
                    <Typography variant="body2">
                      {(stats.performance.classification_latency_p95 || 0).toFixed(0)}ms
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tools Section */}
      <Grid container spacing={2}>
        {/* Test Classification Tool */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Science color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Test Classification</Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Test how the classifier would categorize an email without actually processing it.
              </Alert>

              <TextField
                fullWidth
                label="Email Content"
                multiline
                rows={4}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Paste email body here..."
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Subject (Optional)"
                value={testSubject}
                onChange={(e) => setTestSubject(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Sender (Optional)"
                value={testSender}
                onChange={(e) => setTestSender(e.target.value)}
                placeholder="sender@example.com"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                startIcon={<Science />}
                onClick={handleTestClassify}
                disabled={testClassifyMutation.isLoading || !testEmail.trim()}
              >
                {testClassifyMutation.isLoading ? 'Testing...' : 'Test Classification'}
              </Button>

              {testResult && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Classification Result:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={testResult.type || 'Unknown'}
                      color={getCategoryColor(testResult.type)}
                    />
                    <Chip
                      label={`${((testResult.confidence || 0) * 100).toFixed(1)}% confidence`}
                      size="small"
                      color={getConfidenceTierColor(testResult.confidence_tier)}
                    />
                    <Chip
                      label={testResult.method || 'N/A'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {testResult.matched_keywords && testResult.matched_keywords.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Matched Keywords:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {testResult.matched_keywords.map((keyword, idx) => (
                          <Chip key={idx} label={keyword} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {testResult.reasoning && (
                    <Typography variant="caption" color="text.secondary">
                      {testResult.reasoning}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bulk Reclassification Tool */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Edit color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Bulk Reclassification</Typography>
              </Box>

              <Alert severity="warning" sx={{ mb: 2 }}>
                This tool allows you to reclassify multiple emails at once. Always test with dry_run first!
              </Alert>

              <FormControlLabel
                control={
                  <Switch
                    checked={bulkReclassifyData.dry_run}
                    onChange={(e) => setBulkReclassifyData(prev => ({
                      ...prev,
                      dry_run: e.target.checked
                    }))}
                  />
                }
                label="Dry Run Mode (Recommended)"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Creator ID (Optional)"
                value={bulkReclassifyData.creator_id}
                onChange={(e) => setBulkReclassifyData(prev => ({
                  ...prev,
                  creator_id: e.target.value
                }))}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Old Category (Optional)</InputLabel>
                <Select
                  value={bulkReclassifyData.old_category}
                  onChange={(e) => setBulkReclassifyData(prev => ({
                    ...prev,
                    old_category: e.target.value
                  }))}
                  label="Old Category (Optional)"
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>
                  {validCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }} required>
                <InputLabel>New Category *</InputLabel>
                <Select
                  value={bulkReclassifyData.new_category}
                  onChange={(e) => setBulkReclassifyData(prev => ({
                    ...prev,
                    new_category: e.target.value
                  }))}
                  label="New Category *"
                >
                  {validCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Sender Pattern (SQL LIKE)"
                value={bulkReclassifyData.sender_pattern}
                onChange={(e) => setBulkReclassifyData(prev => ({
                  ...prev,
                  sender_pattern: e.target.value
                }))}
                placeholder="%@example.com"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Subject Pattern (SQL LIKE)"
                value={bulkReclassifyData.subject_pattern}
                onChange={(e) => setBulkReclassifyData(prev => ({
                  ...prev,
                  subject_pattern: e.target.value
                }))}
                placeholder="%partnership%"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Reason"
                value={bulkReclassifyData.reason}
                onChange={(e) => setBulkReclassifyData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
                placeholder="Why are you reclassifying these emails?"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<Edit />}
                onClick={handleBulkReclassify}
                disabled={bulkReclassifyMutation.isLoading || !bulkReclassifyData.new_category}
              >
                {bulkReclassifyMutation.isLoading 
                  ? 'Processing...' 
                  : bulkReclassifyData.dry_run 
                    ? 'Preview Matches' 
                    : 'Apply Reclassification'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration Management */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Settings color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Classifier Configuration</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={() => setConfigDialogOpen(true)}
                >
                  Configure
                </Button>
              </Box>

              {configLoading ? (
                <LinearProgress />
              ) : config?.config ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Confidence Threshold
                    </Typography>
                    <Typography variant="h5">
                      {((config.config.confidence_threshold || 0.60) * 100).toFixed(0)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Hybrid Classifier
                    </Typography>
                    <Typography variant="h5">
                      {config.config.enable_hybrid_classifier ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      API Timeout
                    </Typography>
                    <Typography variant="h5">
                      {config.config.timeout_seconds || 30}s
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="text.secondary">Configuration not available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Classifier Configuration</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Changes require service restart to take effect!
          </Alert>

          <TextField
            fullWidth
            type="number"
            label="Confidence Threshold"
            value={configData.confidence_threshold}
            onChange={(e) => setConfigData(prev => ({
              ...prev,
              confidence_threshold: parseFloat(e.target.value)
            }))}
            inputProps={{ min: 0, max: 1, step: 0.05 }}
            helperText="Minimum confidence score (0.0 - 1.0)"
            sx={{ mb: 2, mt: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={configData.enable_hybrid_classifier}
                onChange={(e) => setConfigData(prev => ({
                  ...prev,
                  enable_hybrid_classifier: e.target.checked
                }))}
              />
            }
            label="Enable Hybrid Classifier"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="API Timeout (seconds)"
            value={configData.timeout_seconds}
            onChange={(e) => setConfigData(prev => ({
              ...prev,
              timeout_seconds: parseInt(e.target.value)
            }))}
            inputProps={{ min: 1, max: 300, step: 5 }}
            helperText="Gmail API timeout"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateConfig}
            disabled={updateConfigMutation.isLoading}
          >
            {updateConfigMutation.isLoading ? 'Updating...' : 'Update Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}